import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Clock, Quote } from 'lucide-react';

const AITransformationDrSmithPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section — Document Style */}
      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-white to-white" />
        <div className="container mx-auto max-w-4xl relative z-10">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight mb-6">
            Why AI Transformation Is{' '}
            <span className="text-blue-600">Imperative for Dental Offices</span>
          </h1>

          <p className="text-xl text-gray-500 max-w-3xl leading-relaxed mb-8">
            Dr. Mark Smith, MD — Chief Medical Officer of the MSU Health
            System — on why he believes every dental office needs to embrace
            AI now, and how he's watched Asha transform practices from the
            inside out.
          </p>

          <div className="flex items-center space-x-4 text-sm text-gray-500 font-medium">
            <div className="flex items-center gap-2">
              <img
                src="/assets/smith.png"
                alt="Dr. Mark Smith"
                className="w-8 h-8 rounded-full object-cover"
              />
              <span>Dr. Mark Smith, MD · CMO, MSU Health System</span>
            </div>
            <span>•</span>
            <span>April 2026</span>
            <span>•</span>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>7 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image + Pull Quote */}
      <section className="px-6 pb-16">
        <div className="container mx-auto max-w-4xl">
          <div className="relative rounded-3xl overflow-hidden mb-12">
            <img
              src="/assets/dentist.jpg"
              alt="Dr. Mark Smith, CMO of MSU Health System"
              className="w-full h-[420px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <p className="text-white text-lg md:text-xl font-medium italic max-w-2xl leading-relaxed">
                "The real cost isn't adopting new technology. The real cost is choosing not to."
              </p>
              <p className="text-white/70 text-sm mt-3 font-medium">
                — Dr. Mark Smith, MD · CMO, MSU Health System
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="px-6 pb-24">
        <div className="container mx-auto max-w-3xl">

          {/* Intro */}
          <div className="mb-16">
            <p className="text-xl text-gray-600 leading-relaxed font-medium mb-6">
              I've spent my career in healthcare leadership, and I've watched practices struggle with the same problems for decades. Staffing shortages. Missed calls. Claims that sit untouched. Patients who fall off the hygiene schedule and never come back.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Most practice owners accept these as the cost of doing business. But after working closely with Asha Health and seeing its impact firsthand across offices in multiple states, I've come to a very different conclusion: <strong className="text-gray-900">the real cost isn't adopting new technology. The real cost is choosing not to.</strong>
            </p>
          </div>

          {/* Section 1 — Hidden Tax */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              The Hidden Tax on Every Dental Practice
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              The average dental office is leaving somewhere between 37% and 65% of its potential revenue on the table. That's not a typo. When you add up the admin burden, the revenue leakage, and the missed opportunities that accumulate every single day, the total drag on a typical practice can approach half of its collections — or more.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              For a practice collecting $100,000 per month, the math breaks down roughly like this:
            </p>

            <div className="space-y-4 mb-8">
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-2">Admin and operations staff</h3>
                <p className="text-gray-600 leading-relaxed">
                  Front desk, billing, office management — run <strong className="text-gray-900">$10,000 to $14,000 per month</strong>, or about 10–14% of collections. And that doesn't account for the hidden costs of turnover: recruiting, training a replacement, and the months of reduced productivity while they get up to speed.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-2">Revenue leakage from missed calls and RCM failures</h3>
                <p className="text-gray-600 leading-relaxed">
                  Unanswered phones, incomplete insurance verifications, improperly submitted claims, unworked denials, sloppy EOB posting — quietly drains another <strong className="text-gray-900">$10,000 to $25,000 per month</strong>. That's 10–25% of collections walking out the door.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <h3 className="font-bold text-gray-900 mb-2">Missed opportunities in scheduling, recall, and case acceptance</h3>
                <p className="text-gray-600 leading-relaxed">
                  The largest bucket of all, costing practices an estimated <strong className="text-gray-900">28–55% of what they could be collecting</strong>. Empty chair time that doesn't get filled. Hygiene patients who lapse because nobody had time to follow up. Treatment plans that patients never move forward on because the practice didn't have the bandwidth to nurture the relationship.
                </p>
              </div>
            </div>

            <div className="bg-white border-2 border-blue-100 rounded-2xl p-6 mb-8">
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                Add it all up: roughly <strong className="text-blue-600">$20,000 to $39,000 per month</strong> in direct admin costs and leakage alone — before you even count the missed-opportunity upside. A well-run practice might land closer to 20% total drag. A typical, disorganized solo practice? North of 30%.
              </p>
            </div>

            <p className="text-gray-600 leading-relaxed">
              The underlying problem in every one of these scenarios is the same: <strong className="text-gray-900">bandwidth</strong>. Your staff simply cannot pick up every phone call 24/7. They cannot dive into a patient's full PMS history, clinical notes, and insurance policy details for every single verification. They cannot spend an hour on hold with a payer for every claim that needs follow-up. They cannot comb through months of prior claims data to determine whether a denial is actually an ortho installment that was posted correctly. The work requires thoroughness, and thoroughness requires time that human teams just don't have.
            </p>
          </div>

          {/* Section 2 — What Asha Does */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              What I've Seen Asha Actually Do
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Through the AI Innovation Fellows Program — a national network of dentists I lead who partner directly with Asha to refine and improve its products — I've had a front-row seat to what happens when a practice adopts this platform. I've personally seen offices add <strong className="text-gray-900">$20,000 or more in additional production per month</strong> after implementation.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              That number isn't theoretical. It comes from real practices in multiple states where Asha's AI agents are now handling the work that used to fall through the cracks: answering every call, verifying insurance with a depth that humans can't match at scale, filling schedule gaps in real time, reactivating lapsed hygiene patients, working denials with full context across prior claims, and following up on treatment plans with the kind of persistent, personalized outreach that builds patient trust.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Asha is on a rapid trajectory toward automating more and more of these workflows until, eventually, the entire administrative side of a dental office can run on autopilot. We're not there yet — but the progress I've seen over the past year has been remarkable, and every month the platform gets meaningfully more capable.
            </p>
          </div>

          {/* Section 3 — Why Asha Is Different */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Why Asha Is Different From Everything Else I've Evaluated
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              I've worked with a lot of technology vendors over the years, and if there's one thing I've learned, it's that saying "AI" is easy. Most of the time, what vendors are actually offering is a chatbot — something that can answer a question if you ask it the right way, but can't actually <em>do</em> anything.
            </p>

            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 mb-8">
              <div className="flex items-start gap-4">
                <Quote className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700 leading-relaxed text-lg">
                  Asha is fundamentally different, and the reason comes down to something they've built called <strong className="text-blue-600">Aria</strong> — the Asha Reasoning and Intelligence Architecture. Aria is what enables Asha's AI to reliably take actions, not just respond to prompts. It can verify insurance, post EOBs, work denials, manage patient communications, and execute complex multi-step workflows the way a highly trained team member would — except it does it 24/7, at scale, without the bandwidth constraints that limit every human team.
                </p>
              </div>
            </div>

            <p className="text-gray-600 leading-relaxed mb-6">
              No other company in the dental space has anything like this, and I don't think that's a coincidence. Most of the established players in dental technology — companies like Dentrix and Eaglesoft — were built by organizations that started out selling equipment. They didn't come from software, let alone AI. Asha's founding team, by contrast, includes AI product leaders with over a decade of experience at frontier technology companies like Google. They didn't pivot to AI because it became a buzzword; they started from AI and built a dental platform around that expertise.
            </p>

            <div className="bg-gray-900 rounded-2xl p-8 text-white">
              <p className="text-lg font-medium leading-relaxed">
                The analogy I use with colleagues is simple: you wouldn't ask a dentist to perform heart surgery. So why would you trust your practice's AI to a company that doesn't actually come from AI?
              </p>
            </div>
          </div>

          {/* Section 4 — The Question */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              The Question Every Practice Owner Should Be Asking
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              When I talk to dentists who are evaluating Asha, the conversation almost always starts with cost. How much does it cost? Can I afford it? But after seeing what I've seen — practices recovering tens of thousands of dollars per month in revenue they didn't even know they were losing — I think the question is backwards.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              <strong className="text-gray-900">The cost isn't Asha. The cost is everything you're leaving on the table by not using it.</strong>
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              If your practice is like most, you're spending 20–35% of your collections on admin labor and operational failures before you even touch clinical overhead, supplies, or rent. That's money that's already gone. The only question is whether you're going to keep losing it, or whether you're going to do something about it.
            </p>
            <p className="text-gray-700 leading-relaxed text-lg font-medium">
              From where I sit, the answer has never been clearer.
            </p>
          </div>

          {/* Divider */}
          <hr className="my-16 border-gray-100" />

          {/* Author Bio */}
          <div className="text-center mb-8">
            <p className="text-gray-500 text-sm leading-relaxed max-w-2xl mx-auto italic">
              Dr. Smith is the Chief Medical Officer of the MSU Health System and leads the Asha AI Innovation Fellows Program, a national initiative bringing together forward-thinking dentists to shape the future of AI-powered dental practice management.
            </p>
          </div>
        </div>
      </article>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gray-900 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-8">
            See What Asha Can Do for Your Practice
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://calendly.com/asha-ai/30min" target="_blank" rel="noopener noreferrer">
              <Button className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-6 rounded-xl text-lg font-bold shadow-lg transition-all duration-300 w-full sm:w-auto">
                Schedule a Call
              </Button>
            </a>
            <a href="/book-demo">
              <Button className="bg-blue-600 hover:bg-blue-500 text-white border border-blue-500 px-8 py-6 rounded-xl text-lg font-bold shadow-lg transition-all duration-300 w-full sm:w-auto">
                Book a Demo
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AITransformationDrSmithPage;
